import { useState, useEffect, useCallback } from 'react';
import MonitorIcon from '@mui/icons-material/Monitor';
import {
  Card,
  Table,
  Stack,
  Button,
  TableBody,
  TableRow,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Grid,
  Box,
  CardHeader,
  CardContent,
  Paper,
  MenuItem,
  TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';
import AuditTableRow from '../components/AuditTableRow';

import { Page, TableSkeleton } from '../../../components';
import { fNumber } from '../../../utils/formatNumber';
import { getAuditRecords } from '../store/auditActions';
import { getAuditBranches } from '../../branches/store/branchActions';
import { CAN_MANAGE_AUDITS } from '../../../shared/auth/roles';
import { getProfile } from '../../../shared/auth/authService';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'location', label: 'Location', alignRight: false },
  { id: 'progress', label: 'Progress', alignRight: false },
  { id: 'findings', label: 'Findings', alignRight: false },
  { id: 'start_date', label: 'Start Date', alignRight: false },
  { id: 'end_date', label: 'End Date', alignRight: false },
  { id: 'auditor', label: 'Auditor', alignRight: false },
  { id: 'completed_date', label: 'Completed On', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
];

export default function AuditView() {
  const dispatch = useDispatch();
  const { isLoading, auditRecords, count } = useSelector((state) => state.auditReducer);
  const { auditBranches } = useSelector((state) => state.branchReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locationId, setLocationId] = useState('');
  const [auditStatus, setAuditStatus] = useState('');

  // Only admins and auditors may open or delete a stock take; the server
  // enforces it, this just keeps the buttons off screens that cannot use them.
  const canManage = CAN_MANAGE_AUDITS.includes(getProfile()?.role);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtering and paging happen on the server, so the list is whatever the last
  // fetch returned. The old screen sorted and sliced a page in the browser and
  // then showed a pager driven by the server's total, which disagreed with it.
  const fetchData = useCallback(
    () => dispatch(getAuditRecords({ page: page + 1, rowsPerPage, locationId, auditStatus })),
    [dispatch, page, rowsPerPage, locationId, auditStatus]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    dispatch(getAuditBranches({}));
  }, [dispatch]);

  return (
    <Page title="Audit Records">
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={4}>
            <Paper elevation={3} sx={{ py: 2, textAlign: 'center' }}>
              <Box sx={{ mb: 0.5 }}>
                <MonitorIcon />
              </Box>
              <Typography variant="h6">{fNumber(count)}</Typography>
              <Typography variant="body2">Stock takes</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <Box sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { sm: 460 } }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Location"
                      value={locationId}
                      onChange={(event) => {
                        setLocationId(event.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">All locations</MenuItem>
                      {auditBranches?.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Status"
                      value={auditStatus}
                      onChange={(event) => {
                        setAuditStatus(event.target.value);
                        setPage(0);
                      }}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  {canManage && (
                    <Button
                      size="small"
                      variant="contained"
                      component={RouterLink}
                      to="/dashboard/audits/new"
                      startIcon={<Iconify icon="eva:plus-fill" />}
                    >
                      New Audit
                    </Button>
                  )}
                </Stack>
              </Box>
              <CardHeader title="Audit Records" subheader="Stock takes and what each one found" />
              <CardContent>
                <Scrollbar>
                  <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                    <Table size="small">
                      <UserListHead headLabel={TABLE_HEAD} rowCount={auditRecords?.length} />
                      <TableBody>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {isLoading ? (
                          <TableSkeleton />
                        ) : auditRecords?.length > 0 ? (
                          auditRecords.map((row) => (
                            <AuditTableRow key={row.id} row={row} canManage={canManage} onRefresh={fetchData} />
                          ))
                        ) : (
                          <TableRow>
                            <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No stock takes yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  component="div"
                  count={count || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
